/** Editor i18n catalog: dashboard (spec #304). English is the key source; German uses "du" and real umlauts. */
import { defineMessages } from "../core.ts";

export const dashboard = defineMessages(
  {
    // Loading, gates and load errors (rendered before the editor's language is known: always English).
    loadingDashboard: "Loading your dashboard…",
    errorDashboard: "Could not load your dashboard ({status}).",
    errorCards: "Could not load your cards.",
    errorEarnings: "Could not load your earnings.",
    signInTitle: "Editor sign-in",
    signInBody: "Sign in with your Aditor account to open your editor workspace.",
    signIn: "Sign in",
    signInFailed: "That sign-in did not complete. Try again.",
    noAccessTitle: "No editor access",
    noAccessBody: "This account is signed in but is not set up as an editor. If you are a client, open your brand portal instead; if you should be an editor, ask the Aditor team to add you.",
    signInOther: "Sign in with another account",

    // Header, account menu, view-as banner.
    viewAsBanner: "Viewing as {name} - admin ({actor}). Read-only, expires {when}.",
    greetNight: "Good night, {name}",
    greetMorning: "Good morning, {name}",
    greetAfternoon: "Good afternoon, {name}",
    greetEvening: "Good evening, {name}",
    greetFallbackName: "there",
    accountMenu: "Account menu",
    menuBilling: "Billing",
    menuProfile: "Profile",
    menuHandbook: "Handbook",
    profileBack: "Dashboard",
    profileTitle: "Profile",
    profileBillingDetails: "Billing details",
    profileBillingDetailsHint: "Legal name, address and bank details for payroll",
    signOut: "Sign out",

    // Why no offers are coming.
    reasonPaused: "You're paused, so no cards are coming right now.",
    reasonAdlingo: "Finish {threshold}% of AdLingo to start getting cards - you're at {percent}% now.",
    reasonOnboarding: "Finish the setup list next to your name to start getting cards.",
    reasonRosterPaused: "Your account is paused with Aditor - reach out if you think this is wrong.",

    // Offer card (card dealer).
    noNewOffers: "No new offers",
    noOffersDefault: "Nothing to answer right now. New cards land here when you qualify for one.",
    offerHereYouGo: "Here you go!",
    offerMoreWaiting: "{count} more waiting",
    expires: "Expires {when}",
    earnStatic: "{amount} · Static",
    earnApprox: "{amount} · approx. {seconds}s",
    specSeconds: "{n}s",
    specHooksOne: "{n} hook",
    specHooksMany: "{n} hooks",
    specVariants: "{n} variants",
    accept: "✓ Accept",
    claiming: "Claiming…",
    decline: "Decline",
    declining: "Declining…",
    declineNote: "Declining releases this card for another editor.",
    offerActionError: "Could not save that - it may already be answered. Refreshing…",

    // Submit tile.
    submitEyebrow: "02 / Submit",
    inProduction: "In production",
    due: "due {when}",
    uploadGrace: "upload grace, {minutes} min left",
    submitForReview: "Submit for review ↗",
    submitting: "Opening…",
    giveBack: "Give back",
    givingBack: "Giving back…",
    giveBackConfirmTitle: "Give this card back?",
    giveBackConfirmBody: "This is not a hand-in. The card leaves you and goes to another editor, and you get no new offers for {hours}h.",
    giveBackConfirmTrust: "It costs {trust} trust.",
    giveBackConfirmFree: "No trust cost.",
    giveBackConfirmYes: "Yes, give it back",
    giveBackConfirmKeep: "Keep the card",
    uploadOpen: "Upload open - not handed in yet",
    uploadOpenLink: "Open upload ↗",
    uploadOpenNote: "It counts as handed in once the file has landed. Closed the tab? Open the upload and send it again.",
    giveBackError: "Could not give this card back - it may already have moved. Refreshing…",
    noCardInProduction: "No card in production",
    acceptToStart: "Accept an offer to start your next card.",
    submitErrNoFolder: "This card has no hand-in folder yet. Ops has been told and will fix it.",
    submitErrUnavailable: "FreeFrame is not reachable right now - try again in a minute.",
    submitErrStageChanged: "This card already moved - refreshing.",
    submitErrRevisionOpen: "You have an open revision. Hand that in first, then this card. Revisions come first, and this card's deadline keeps running.",
    submitErrGeneric: "Could not submit - try again.",
    driveNewRound: "New version",
    driveNewRoundNote: "Client feedback on Trello? Paste the link to the new version. It goes to the client as the next round.",
    driveSubmit: "Hand in",
    driveHeading: 'Hand in "{title}"',
    driveLine: "This client gets their videos through Drive.",
    drivePlaceholder: "Paste the Google Drive link",
    driveHint: "The portal checks that the link opens without a login and holds a video before it counts as handed in.",
    driveHandIn: "Hand in",
    driveChecking: "Checking the link…",
    driveCancel: "Cancel",
    driveErrInvalid: "That is not a Google Drive link. Paste the folder or file link from drive.google.com.",
    driveErrNotPublic: "This link needs a login. In Drive, share it with \"Anyone with the link\", then hand in again.",
    driveErrNoVideo: "There is no video behind this link. Upload the finished videos to the folder, then hand in again. Nothing was counted.",
    driveErrSame: "This is the link from the last round. A revision needs a new link.",
    driveErrUnavailable: "Drive cannot be read right now. Nothing was handed in - try again in a minute, your time counts from this try.",

    // Earnings tile.
    earningsEyebrow: "03 / Earnings",
    cardCountOne: "{count} card",
    cardCountMany: "{count} cards",
    earningsPeriod: "{period}, updated after each hand-in.",
    viewBilling: "View billing",

    // Adlingo, mentorship, time off.
    adlingoAllDone: "All caught up.",
    adlingoLessons: "{count}/{total} lessons",
    adlingoOpen: "Open training",
    adlingoProgress: "Learning progress",
    adlingoPose1: "The origin story.",
    adlingoPose2: "Case opened.",
    adlingoPose3: "Training montage.",
    adlingoPose4: "Cool under pressure.",
    adlingoPose5: "A little edit magic.",
    adlingoPose6: "Into the next scene.",
    adlingoPose7: "The director’s cut.",
    adlingoPose8: "Hero mode.",
    adlingoPose9: "One more take.",
    adlingoPose10: "The final boss.",
    mentorTitle: "Mentorship & calls",
    mentorCalendar: "Calendar ↗",
    mentorViewCalendar: "View training calendar →",
    timeOff: "Time off",
    timeOffAdd: "+ Add",
    timeOffNone: "No upcoming time off booked.",
    timeOffStart: "Start date",
    timeOffEnd: "End date",
    timeOffNote: "Note (optional)",
    timeOffSaveError: "Could not save that time off. Check the dates and try again.",
    timeOffRemoveError: "Could not remove that - refreshing…",

    // Revisions list, card focus view.
    revisionsTitle: "Revisions ({count})",
    untitledCard: "Untitled card",
    review: "Review",
    handIn: "Hand in ↗",
    stageHandedIn: "Handed in",
    stageDelivered: "Delivered",
    stageHandedOver: "Handed over",
    cardUnavailable: "Card not available",
    cardUnavailableBody: "This card isn't available - it may not be yours, or it may no longer exist.",
    cardLookupFailed: "This card could not be looked up right now. Open the link again in a moment.",
    backToDashboard: "Back to your dashboard",
  },
  {
    loadingDashboard: "Dein Dashboard wird geladen…",
    errorDashboard: "Dein Dashboard konnte nicht geladen werden ({status}).",
    errorCards: "Deine Cards konnten nicht geladen werden.",
    errorEarnings: "Dein Verdienst konnte nicht geladen werden.",
    signInTitle: "Editor-Anmeldung",
    signInBody: "Melde dich mit deinem Aditor-Konto an, um deinen Editor-Workspace zu öffnen.",
    signIn: "Anmelden",
    signInFailed: "Die Anmeldung hat nicht geklappt. Versuch es noch mal.",
    noAccessTitle: "Kein Editor-Zugang",
    noAccessBody: "Dieses Konto ist angemeldet, aber nicht als Editor eingerichtet. Wenn du Kunde bist, öffne stattdessen dein Brand-Portal; wenn du Editor sein solltest, bitte das Aditor-Team, dich hinzuzufügen.",
    signInOther: "Mit einem anderen Konto anmelden",

    viewAsBanner: "Ansicht als {name} - Admin ({actor}). Nur Lesezugriff, endet {when}.",
    greetNight: "Gute Nacht, {name}",
    greetMorning: "Guten Morgen, {name}",
    greetAfternoon: "Guten Tag, {name}",
    greetEvening: "Guten Abend, {name}",
    greetFallbackName: "du",
    accountMenu: "Kontomenü",
    menuBilling: "Abrechnung",
    menuProfile: "Profil",
    menuHandbook: "Handbuch",
    profileBack: "Dashboard",
    profileTitle: "Profil",
    profileBillingDetails: "Rechnungsdaten",
    profileBillingDetailsHint: "Bürgerlicher Name, Adresse und Bankverbindung für die Abrechnung",
    signOut: "Abmelden",

    reasonPaused: "Du bist pausiert, deshalb kommen gerade keine Cards.",
    reasonAdlingo: "Schließ {threshold}% von AdLingo ab, um Cards zu bekommen - du bist gerade bei {percent}%.",
    reasonOnboarding: "Schließ die Einrichtung neben deinem Namen ab, um Cards zu bekommen.",
    reasonRosterPaused: "Dein Konto ist bei Aditor pausiert - melde dich, wenn du glaubst, dass das nicht stimmt.",

    noNewOffers: "Keine neuen Angebote",
    noOffersDefault: "Gerade gibt es nichts zu beantworten. Neue Cards landen hier, sobald eine zu dir passt.",
    offerHereYouGo: "Bitte schön!",
    offerMoreWaiting: "{count} weitere warten",
    expires: "Läuft {when} ab",
    earnStatic: "{amount} · Statisch",
    earnApprox: "{amount} · ca. {seconds} s",
    specSeconds: "{n} s",
    specHooksOne: "{n} Hook",
    specHooksMany: "{n} Hooks",
    specVariants: "{n} Varianten",
    accept: "✓ Annehmen",
    claiming: "Wird angenommen…",
    decline: "Ablehnen",
    declining: "Wird abgelehnt…",
    declineNote: "Wenn du ablehnst, geht die Card an einen anderen Editor.",
    offerActionError: "Das hat nicht geklappt - vielleicht ist die Card schon vergeben. Wird neu geladen…",

    submitEyebrow: "02 / Abgabe",
    inProduction: "In Produktion",
    due: "fällig {when}",
    uploadGrace: "Upload-Kulanz, noch {minutes} Min.",
    submitForReview: "Zur Prüfung einreichen ↗",
    submitting: "Wird geöffnet…",
    giveBack: "Card zurückgeben",
    givingBack: "Wird zurückgegeben…",
    giveBackConfirmTitle: "Card zurückgeben?",
    giveBackConfirmBody: "Das ist keine Abgabe. Die Card geht weg von dir an jemand anderen, und du bekommst {hours} h lang keine neuen Angebote.",
    giveBackConfirmTrust: "Das kostet {trust} Trust.",
    giveBackConfirmFree: "Kein Trust-Abzug.",
    giveBackConfirmYes: "Ja, Card zurückgeben",
    giveBackConfirmKeep: "Card behalten",
    uploadOpen: "Upload offen - noch nicht abgegeben",
    uploadOpenLink: "Upload öffnen ↗",
    uploadOpenNote: "Abgegeben ist die Card erst, wenn die Datei angekommen ist. Tab geschlossen? Öffne den Upload und schick sie nochmal.",
    giveBackError: "Die Card konnte nicht zurückgegeben werden - vielleicht ist sie schon weiter. Wird neu geladen…",
    noCardInProduction: "Keine Card in Produktion",
    acceptToStart: "Nimm ein Angebot an, um deine nächste Card zu starten.",
    submitErrNoFolder: "Für diese Card gibt es noch keinen Abgabe-Ordner. Ops weiß Bescheid und kümmert sich.",
    submitErrUnavailable: "FreeFrame ist gerade nicht erreichbar - versuch es in einer Minute noch mal.",
    submitErrStageChanged: "Diese Card ist schon weiter - wird neu geladen.",
    submitErrRevisionOpen: "Du hast eine offene Revision. Reich zuerst die ein, dann diese Card. Revisionen gehen vor, und die Deadline dieser Card läuft weiter.",
    submitErrGeneric: "Abgabe fehlgeschlagen - versuch es noch mal.",
    driveNewRound: "Neue Version",
    driveNewRoundNote: "Feedback vom Kunden auf Trello? Füg den Link zur neuen Version ein. Er geht als nächste Runde an den Kunden.",
    driveSubmit: "Einreichen",
    driveHeading: "„{title}“ einreichen",
    driveLine: "Dieser Kunde bekommt seine Videos über Drive.",
    drivePlaceholder: "Google-Drive-Link einfügen",
    driveHint: "Das Portal prüft, ob der Link ohne Login aufgeht und ein Video enthält, bevor die Card als eingereicht zählt.",
    driveHandIn: "Einreichen",
    driveChecking: "Link wird geprüft…",
    driveCancel: "Abbrechen",
    driveErrInvalid: "Das ist kein Google-Drive-Link. Füg den Ordner- oder Datei-Link von drive.google.com ein.",
    driveErrNotPublic: "Dieser Link braucht einen Login. Gib ihn in Drive für „Jeder, der den Link hat“ frei und reich dann noch mal ein.",
    driveErrNoVideo: "Hinter diesem Link liegt kein Video. Lad die fertigen Videos in den Ordner und reich dann noch mal ein. Nichts wurde gezählt.",
    driveErrSame: "Das ist der Link aus der letzten Runde. Eine Revision braucht einen neuen Link.",
    driveErrUnavailable: "Drive ist gerade nicht lesbar. Nichts wurde eingereicht - versuch es in einer Minute noch mal, deine Zeit zählt ab diesem Versuch.",

    earningsEyebrow: "03 / Verdienst",
    cardCountOne: "{count} Card",
    cardCountMany: "{count} Cards",
    earningsPeriod: "{period}, aktualisiert nach jeder Abgabe.",
    viewBilling: "Zur Abrechnung",

    adlingoAllDone: "Alles geschafft.",
    adlingoLessons: "{count}/{total} Lektionen",
    adlingoOpen: "Training öffnen",
    adlingoProgress: "Lernfortschritt",
    adlingoPose1: "Die Origin-Story.",
    adlingoPose2: "Der Fall ist eröffnet.",
    adlingoPose3: "Zeit fürs Training.",
    adlingoPose4: "Cool unter Druck.",
    adlingoPose5: "Ein bisschen Schnittmagie.",
    adlingoPose6: "Auf zur nächsten Szene.",
    adlingoPose7: "Der Director’s Cut.",
    adlingoPose8: "Im Heldenmodus.",
    adlingoPose9: "Noch ein Take.",
    adlingoPose10: "Der Endgegner.",
    mentorTitle: "Mentoring & Calls",
    mentorCalendar: "Kalender ↗",
    mentorViewCalendar: "Trainingskalender ansehen →",
    timeOff: "Abwesenheit",
    timeOffAdd: "+ Eintragen",
    timeOffNone: "Keine Abwesenheit eingetragen.",
    timeOffStart: "Startdatum",
    timeOffEnd: "Enddatum",
    timeOffNote: "Notiz (optional)",
    timeOffSaveError: "Die Abwesenheit konnte nicht gespeichert werden. Prüf die Daten und versuch es noch mal.",
    timeOffRemoveError: "Konnte nicht entfernt werden - wird neu geladen…",

    revisionsTitle: "Revisionen ({count})",
    untitledCard: "Card ohne Titel",
    review: "Feedback ansehen",
    handIn: "Abgeben ↗",
    stageHandedIn: "Abgegeben",
    stageDelivered: "Geliefert",
    stageHandedOver: "Übergeben",
    cardUnavailable: "Card nicht verfügbar",
    cardUnavailableBody: "Diese Card ist nicht verfügbar - vielleicht gehört sie nicht dir oder es gibt sie nicht mehr.",
    cardLookupFailed: "Diese Card lässt sich gerade nicht finden. Öffne den Link gleich noch einmal.",
    backToDashboard: "Zurück zu deinem Dashboard",
  },
);
